import { useState, useEffect } from 'react';
import { isEmpty } from 'underscore';
import moment from 'moment';
import { formatDate } from '../../../../utils/common';
import { CartTableItem } from '../../../../types/home';
import { ShoppingItem, SummaryPriceItem } from '../../../../types/shopping';
import { IDatasetBrief } from '../../../../types/metadata';
import useCartStore from "../../../../store/cartStore";
import useDatasetStore from "../../../../store/datasetStore";
import useAuthStore from "../../../../store/authStore";
import useGetShoppingCartQuery from "../../../../queries/useGetShoppingCartQuery";

export default function useShoppingCartData() {
  const setShoppingItems = useCartStore(state => state.setShoppingItems);
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs);
  const user = useAuthStore(state => state.user);
  
  const [cartData, setCartData] = useState<CartTableItem[]>([]);
  const [shoppingPrice, setShoppingPrice] = useState<SummaryPriceItem>({} as SummaryPriceItem);
  
  const shoppingCart = useGetShoppingCartQuery(true, true);
  
  useEffect(() => {
    const isLoggedIn = !isEmpty(user);
    if (!isLoggedIn) {
      setShoppingPrice({} as SummaryPriceItem);
    } else {
      prepareData(false);
    }
  }, [user, shoppingCart?.data]);
  
  const updateTableData = (
    items: { [key: string]: { [key: string]: ShoppingItem } },
    showPending: boolean
  ) => {
    const result: CartTableItem[] = [];
    Object.keys(items).forEach((pointName: string) => {
      const itemsPerPoint = items[pointName];
      Object.keys(itemsPerPoint).forEach((category: string) => {
        const item = itemsPerPoint[category];
        const hasError = item.extractionStatus === 'Error';
        const isPending = item.extractionStatus === 'Pending';
        const brief = datasetBriefs.find((brief: IDatasetBrief) => brief.Id === item.dataset_id);
        result.push({
          id: item.id,
          key: item.order_id,
          datasetId: item.dataset_id,
          Dataset: item.dataset_id,
          Location: item.location,
          TimeStr: `${formatDate(moment(item.start_date))} - ${formatDate(
            moment(item.end_date)
          )}`,
          Price: item.price,
          Detail: [],
          showDetail: false,
          Operation: null,
          loading: false,
          isEdit: false,
          pointName: item.point_name,
          orderId: item.order_id,
          category: item.dataset_category,
          acronym: item.dataset_acronym,
          hasError: !showPending
            ? hasError
            : hasError || isPending,
          isPending: isPending,
          isFwe: brief?.IsFWE
        });
      });
    });

    setCartData(result);
  };

  async function prepareData(showPendingItems: boolean) {
    const shoppingCartData = shoppingCart?.data;

    if (!shoppingCartData) {
      return;
    }

    setShoppingItems(shoppingCartData.shoppingItems);
    setShoppingPrice({
      totalPrice: shoppingCartData.totalPrice,
      discount: shoppingCartData.discount,
      subtotal: shoppingCartData.subtotal,
    });
    updateTableData(shoppingCartData.shoppingItems, showPendingItems);
  }

  const setRowLoading = (orderId: string, isLoading: boolean) => {
    setCartData((data) => {
      const updatedData = [...data];
      const item = updatedData.find(c => c.orderId === orderId);
      if (item) {
        item.loading = isLoading;
      }
      return updatedData;
    });
  };

  return {
    cartData,
    setCartData,
    shoppingPrice,
    prepareData,
    setRowLoading,
    shoppingCartData: shoppingCart?.data
  };
}
